import { useEffect, useState } from 'react'
import { GetStaticPaths } from 'next'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { Durations } from '@/constants/durations'
import { CollectiblesLayout } from '@/layouts/CollectiblesLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getWebClient } from '@/services/ApolloClient'
import { getAvailableCollectibleOffersByProject } from '@/services/ProjectsService/ProjectsService'
import { GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT } from '@/services/ProjectsService/queries'
import { CollectibleOffer, GetAvailableCollectibleOffersByProjectQuery } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { CollectiblesPacksView } from '@/views/CollectiblesViews/CollectiblesPacksViews'

interface CollectiblePacksDetailsViewProps {
  locale: string
  serverOffers: GetAvailableCollectibleOffersByProjectQuery['availableCollectibleOffers']
  projectId: string
}

export const CollectiblePacksDetailsView: NextPageWithLayout<CollectiblePacksDetailsViewProps> = ({
  locale,
  serverOffers,
  projectId,
}) => {
  const [offers, setOffers] = useState<CollectibleOffer[]>((serverOffers as CollectibleOffer[]) ?? [])
  const client = getWebClient({ locale })

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const fetchedOffers = await client.query({
          query: GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT,
          variables: { input: { projectId: projectId } },
          errorPolicy: 'all',
        })
        setOffers(fetchedOffers.data?.availableCollectibleOffers)
      } catch (error) {
        logger().warn(`Error fetching available collectible offers for project: ${projectId}`, { projectId }, error)
      }
    }

    fetchOffers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers])

  if (!offers || offers?.length === 0) return null

  const packOffers = offers?.filter(
    (offer) =>
      offer?.products?.every((product) => product?.__typename === 'CollectibleProductPack') &&
      offer.products?.length > 0,
  ) as CollectibleOffer[]
  const keepsakeOffers = offers?.filter((offer) =>
    offer?.products?.some((product) => product?.__typename === 'CollectibleProductKeepsake'),
  ) as CollectibleOffer[]
  const signatureOffers = offers?.filter((offer) =>
    offer?.products?.some((product) => product?.__typename === 'CollectibleProductSignature'),
  ) as CollectibleOffer[]
  const momentOffers = offers?.filter((offer) =>
    offer?.products?.some((product) => product?.__typename === 'CollectibleProductMoment'),
  ) as CollectibleOffer[]

  return (
    <>
      <PaddedContainer>
        <CollectiblesPacksView
          packOffers={packOffers}
          keepsakeOffers={keepsakeOffers}
          signatureOffers={signatureOffers}
          momentOffers={momentOffers}
          offers={offers}
          projectId={projectId as string}
        />
      </PaddedContainer>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<CollectiblePacksDetailsViewProps>(
  { filePath: 'pages/[locale]/collectibles/[projectId]/shop/index.tsx' },
  async ({ federationClient, locale, next404, params }) => {
    const projectId = getItemOrFirstEntry(params.projectId)
    if (!projectId) return next404()

    const [offers, translations] = await Promise.all([
      getAvailableCollectibleOffersByProject({ input: { projectId: projectId } }, federationClient),
      loadTranslations(locale, ['common', 'collectibles-marketplace', 'account', 'collectibles', 'pif']),
    ])

    return {
      props: {
        locale,
        serverOffers: offers,
        projectId: projectId,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

CollectiblePacksDetailsView.getLayout = (page) => {
  return (
    <CollectiblesLayout title="Collectible Scenes" isDarkMode={false}>
      {page}
    </CollectiblesLayout>
  )
}

export default CollectiblePacksDetailsView
